import * as Popover from "@radix-ui/react-popover";
import { Building2, Check, ChevronDown, Globe, Search } from "lucide-react";
import { useId, useMemo, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import type { Organization } from "@/types";
import { cn } from "@/utils/cn";

/** Value used for "Global (All Organizations)" when `allowGlobal` is set. */
export const GLOBAL_ORG = "__all__";

interface OrgPickerProps {
	orgs: Organization[] | undefined;
	value: string;
	onChange: (orgId: string) => void;
	placeholder?: string;
	/** Offer "Global (All Organizations)" as the first option. */
	allowGlobal?: boolean;
	className?: string;
}

/**
 * Organization picker with search. A plain select listed 65 organizations in
 * one scrolling menu; this one filters by name or slug as you type. Arrow keys
 * move, Enter picks, Escape closes. The slug and plan are shown so that
 * similar names (two "Lumiere ..." organizations) can be told apart.
 */
export function OrgPicker({
	orgs,
	value,
	onChange,
	placeholder = "Select organization...",
	allowGlobal = false,
	className,
}: OrgPickerProps) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [active, setActive] = useState(0);
	const listRef = useRef<HTMLUListElement>(null);
	const listId = useId();

	type Option = { id: string; org?: Organization };
	const options = useMemo<Option[]>(() => {
		const q = query.trim().toLowerCase();
		const matches = (orgs ?? []).filter(
			(o) =>
				!q ||
				o.name.toLowerCase().includes(q) ||
				(o.slug ?? "").toLowerCase().includes(q),
		);
		const list: Option[] = matches.map((org) => ({ id: org._id, org }));
		return allowGlobal && !q ? [{ id: GLOBAL_ORG }, ...list] : list;
	}, [orgs, query, allowGlobal]);

	const selected = orgs?.find((o) => o._id === value);
	const label =
		value === GLOBAL_ORG && allowGlobal
			? "Global (All Organizations)"
			: (selected?.name ?? "");

	const pick = (id: string) => {
		onChange(id);
		setOpen(false);
		setQuery("");
	};

	const move = (to: number) => {
		const next = Math.max(0, Math.min(options.length - 1, to));
		setActive(next);
		listRef.current
			?.querySelector<HTMLElement>(`[data-index="${next}"]`)
			?.scrollIntoView({ block: "nearest" });
	};

	const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "ArrowDown") {
			e.preventDefault();
			move(active + 1);
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			move(active - 1);
		} else if (e.key === "Enter") {
			e.preventDefault();
			const option = options[active];
			if (option) pick(option.id);
		}
	};

	return (
		<Popover.Root
			open={open}
			onOpenChange={(o) => {
				setOpen(o);
				if (o) {
					setQuery("");
					setActive(0);
				}
			}}
		>
			<Popover.Trigger asChild>
				<button
					type="button"
					aria-haspopup="listbox"
					aria-expanded={open}
					className={cn(
						"flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
						className,
					)}
				>
					<span
						className={cn(
							"flex min-w-0 items-center gap-2 truncate",
							!label && "text-muted-foreground",
						)}
					>
						{label && value !== GLOBAL_ORG && (
							<Building2 className="h-4 w-4 shrink-0" />
						)}
						{label || placeholder}
					</span>
					<ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
				</button>
			</Popover.Trigger>
			<Popover.Portal>
				<Popover.Content
					align="start"
					sideOffset={4}
					className="z-50 w-[var(--radix-popover-trigger-width)] min-w-[280px] rounded-md border bg-white p-1 shadow-md"
					onOpenAutoFocus={(e) => {
						// Focus the search box, not the first option.
						e.preventDefault();
						(e.currentTarget as HTMLElement)
							.querySelector<HTMLInputElement>("input")
							?.focus();
					}}
				>
					<div className="flex items-center gap-2 border-b px-2 pb-1">
						<Search className="h-4 w-4 shrink-0 text-muted-foreground" />
						<input
							role="combobox"
							aria-expanded={open}
							aria-controls={listId}
							aria-activedescendant={
								options[active] ? `${listId}-${active}` : undefined
							}
							aria-label="Search organizations"
							value={query}
							onChange={(e) => {
								setQuery(e.target.value);
								setActive(0);
							}}
							onKeyDown={onKeyDown}
							placeholder="Search by name or slug..."
							className="h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
						/>
					</div>
					<ul
						ref={listRef}
						id={listId}
						role="listbox"
						className="max-h-72 overflow-y-auto py-1"
					>
						{options.length === 0 && (
							<li className="px-3 py-6 text-center text-sm text-muted-foreground">
								No organization matches "{query}"
							</li>
						)}
						{options.map((option, i) => {
							const isSelected = option.id === value;
							return (
								<li
									key={option.id}
									id={`${listId}-${i}`}
									data-index={i}
									role="option"
									aria-selected={isSelected}
									onMouseEnter={() => setActive(i)}
									onMouseDown={(e) => e.preventDefault()}
									onClick={() => pick(option.id)}
									className={cn(
										"flex cursor-pointer items-center gap-2 rounded-sm px-2 py-2 text-sm",
										i === active && "bg-gray-100",
									)}
								>
									<Check
										className={cn(
											"h-4 w-4 shrink-0",
											isSelected ? "opacity-100" : "opacity-0",
										)}
									/>
									{option.org ? (
										<>
											<Building2 className="h-4 w-4 shrink-0 text-gray-400" />
											<span className="min-w-0 flex-1 truncate">
												{option.org.name}
												{option.org.slug && (
													<span className="ml-2 text-xs text-muted-foreground">
														{option.org.slug}
													</span>
												)}
											</span>
											<Badge variant="outline" className="shrink-0">
												{option.org.plan}
											</Badge>
										</>
									) : (
										<>
											<Globe className="h-4 w-4 shrink-0 text-gray-400" />
											<span>Global (All Organizations)</span>
										</>
									)}
								</li>
							);
						})}
					</ul>
				</Popover.Content>
			</Popover.Portal>
		</Popover.Root>
	);
}
